import { TextField, Link } from 'office-ui-fabric-react';
import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import PostMessageReceiver from '../PostMessageReceiver';
import { InfoBlock } from './InfoBlock';
import { Dictionary } from '../../../entities/common';
import { Validator } from '../../../validation';
import { oauthTarget } from './JiraConnectionEdit';
import { IJiraConnectionInfo, JiraOAuth20State } from '../../../store/integration/JiraStore';

const OAuth2Config = {
    getAppRegistrationUrl: (url: string) => `${url}/plugins/servlet/applinks/listApplicationLinks`,
    ApplicationUrl: `https://${window.location.host}`,
    getAuthorizeUrl: (siteUrl: string, clientId: string, clientSecret: string) => {
        const query = new URLSearchParams();
        const data: Dictionary<string | undefined> = {
            connectionId: undefined,
            siteUrl: siteUrl,
            clientId: clientId,
            clientSecret: clientSecret,
        };
        Object.entries(data).forEach(([key, value]) => value && query.set(key, value));
        return `${OAuth2Config.ApplicationUrl}/api/integration/jira/auth/oauth20?${query.toString()}`;
    }
}

const validation = {
    oAuth2ClientId: Validator.new().required().build(),
    oAuth2ClientSecret: Validator.new().required().build(),
}

type OAuth20AuthControlProps = {
    url: string;
    oauth20: JiraOAuth20State;
    clientId?: string;
    clientSecret?: string;
    setError: (error?: string) => void;
    onSuccess: (data: Partial<IJiraConnectionInfo>) => void;
}

interface PostMessageData {
    context: {
        error?: { message: string },
        auth?: any
    }
}

export const OAuth20AuthControl = connect((state: ApplicationState) => ({ oauth20: state.jira.oauth20 }))((props: OAuth20AuthControlProps) => {
    const [showAppRegistrationInfo, setShowAppRegistrationInfo] = React.useState(false);
    const [clientId, setClientId] = React.useState(props.clientId);
    const [clientSecret, setClientSecret] = React.useState(props.clientSecret);
    const { url } = props;

    const onAuthAuthorize = (): void => {
        if (!clientId || !clientSecret) {
            return;
        }

        PostMessageReceiver.openPopupWindow(
            OAuth2Config.getAuthorizeUrl(url, clientId, clientSecret),
            oauthTarget,
            { width: 800, height: 550 });
    };

    const _onOAuthMessageReceived = React.useCallback((e: MessageEvent, data: PostMessageData) => {
        if (data.context.auth) {
            props.onSuccess({
                oAuth20ClientId: clientId!,
                oAuth20ClientSecret: clientSecret!,
                oAuth20Token: data.context.auth.access_token,
                oAuth20RefreshToken: data.context.auth.refresh_token,
                oAuth20ExpiresIn: data.context.auth.expires_in,
                oAuth20CreatedAt: data.context.auth.created_at,
            });
        }
        else if (data.context.error?.message) {
            props.setError(data.context.error.message);
        }
    }, [clientId, clientSecret]);

    return <>
        <PostMessageReceiver<PostMessageData> from={oauthTarget} onMessageReceived={_onOAuthMessageReceived} />
        <TextField
            value={clientId}
            label="Client ID"
            placeholder="Client ID"
            onChange={(e, _) => { 
                setClientId(_);
                props.setError();
            }}
            onGetErrorMessage={validation.oAuth2ClientId.getErrorMessage} />
        <TextField
            value={clientSecret}
            type="password"
            label="Client secret"
            placeholder="Client secret"
            onChange={(e, _) => {
                setClientSecret(_);
                props.setError();
            }}
            onGetErrorMessage={validation.oAuth2ClientSecret.getErrorMessage} />

        <div className="description">
            If you already registered your Jira app,
            please <Link className="link" onClick={onAuthAuthorize}>Authorize</Link>.
            If you don't -
            click <Link className="link" onClick={() => setShowAppRegistrationInfo(true)}>App Registration</Link> first.
        </div>
        {showAppRegistrationInfo && <div className="oauth-info">
            <div className="title">App Registration</div>
            <div className="description">
                Please follow
                the <Link target="_blank" href={OAuth2Config.getAppRegistrationUrl(url)}>link</Link> and
                register Jira app using the following app registration details
            </div>
            {<InfoBlock name="Step 1" lines={[
                { name: 'External application', value: "Yes", hideCopy: true },
                { name: 'Incoming', value: "Yes", hideCopy: true }
            ]} />}
            {<InfoBlock name="Step 2" lines={[
                { name: 'Redirect URL', value: props.oauth20.redirectUri },
                { name: 'Application permissions', value: props.oauth20.scope, hideCopy: true },
            ]} />}
        </div>}
    </>;
});
